<template>
  <div class="survey-page">
    <modal-loading
      v-if="isLoading || isSubmitting"
      :openModal="isLoading || isSubmitting"
      :value="100"
      :label="isSubmitting ? 'enviando avaliação...' : 'carregando pesquisa...'"
      :variant="'white'"
      :animated="false"
    />

    <div v-if="!isLoading" class="survey-page-wrapper">
      <header class="survey-hero">
        <div class="sales-page-profile">
          <div class="sales-page-profile__cover"></div>
          <div class="survey-container">
            <div class="sales-page-profile__card">
              <div v-if="headerLogoUrl" class="sales-page-profile__logo">
                <img
                  :src="headerLogoUrl"
                  :alt="productFound ? placeName || 'Logo' : 'Janoo'"
                />
              </div>
              <p v-if="!productFound" class="survey-invalid-message">
                Este link está incorreto.
              </p>
              <div
                v-else-if="productNameHTML"
                class="survey-title-block"
                v-html="productNameHTML"
              ></div>
              <p v-else class="sales-page-profile__text">
                Pesquisa de satisfação
              </p>
            </div>
          </div>
        </div>
      </header>

      <div v-if="productFound" class="survey-container survey-content">
        <div class="survey-card">
          <div class="survey-form-body">
          <div v-if="isSubmitted" class="survey-success text-center py-4">
            <i class="far fa-check-circle survey-success-icon mb-3"></i>
            <h2 class="survey-success-title">Obrigado pela sua avaliação!</h2>
            <p class="text-muted mb-0">
              Sua resposta foi registrada e ajuda a melhorar a experiência neste
              lugar.
            </p>
          </div>
          <EditXFieldModal
            v-else-if="hasSurveyFields"
            layout-mode="page"
            :object="{}"
            core-object="Survey"
            :current-x-field-detail="''"
            :x-fields="xFields"
            :x-fields-data="xFieldsData"
            @saveXFieldsData="onSubmitSurvey"
          />
          <div v-else class="survey-empty text-muted text-center py-4">
            Formulário da pesquisa indisponível no momento.
          </div>
        </div>

          <footer class="survey-footer">
            <hr class="survey-divider" />
            <content-footer textAlign="center" backgroundColor="transparent" />
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import EditXFieldModal from '@/views/Components/EditXFieldModal.vue'
import ContentFooter from '@/views/Layout/ContentFooter.vue'
import ModalLoading from '@/components/ModalLoading/ModalLoading.vue'
import nodeService from '@/services/nodejs/index'
import { publicLogoSource } from '@/store/utils/placePublicData'
import surveyXFieldsFallback from '@/views/Products/store/survey-xfields.json'

/**
 * Mapa place_uuid → nome amigável do coletor (legado Zoho).
 * O collectorId efetivo vem da rota ou de INTERNAL_{product.id}.
 */
const PLACE_COLLECTORS = {
  '2F2BCB3C-A778-11EB-9702-5254006CC309': {
    collectorId: '4XCUO9',
    collectorName: 'Satisfação Baixio',
  },
  '2F249056-A778-11EB-9702-5254006CC309': {
    collectorId: 'SND4nS',
    collectorName: 'Satisfação Cachoeira do Buracão',
  },
  '481E9F40-4B07-11ED-8534-F23C9227EC8D': {
    collectorId: 'A0D4w3',
    collectorName: 'Satisfação Museu do Garimpo',
  },
  '5662C894-ABAE-11EC-8BF6-F23C9227EC8D': {
    collectorId: 'NM0uip',
    collectorName: 'Satisfação Parque Da Muritiba',
  },
}

export default {
  name: 'SurveyPage',

  components: {
    EditXFieldModal,
    ContentFooter,
    ModalLoading,
  },

  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      isSubmitted: false,
      product: {},
      xFields: {},
      xFieldsData: {},
      coreObject: 'Survey',
      placeLogoUrl: null,
      placeLogoObjectUrl: null,
      janooLogo: require('@/assets/img/j-logo.png'),
    }
  },

  computed: {
    ...mapGetters('auth', {
      userDetails: 'getUserDetails',
      params: 'params',
    }),

    ...mapGetters('booking', {
      place_public_data: 'getPlacePublicData',
    }),

    placeName() {
      return this.place_public_data &&
        this.place_public_data.place &&
        this.place_public_data.place.name
        ? this.place_public_data.place.name
        : ''
    },

    productFound() {
      return !!(this.product && this.product.uuid)
    },

    headerLogoUrl() {
      if (!this.productFound) return this.janooLogo
      return this.placeLogoUrl
    },

    productUuid() {
      return this.$route.params.product_uuid || ''
    },

    collectorId() {
      return this.$route.params.collector_id || ''
    },

    productName() {
      const placePart = this.product.vendor_name
        ? this.product.product_place_name
        : ''
      const productPart = this.product.product_name || ''
      if (placePart && productPart) return `${placePart} - ${productPart}`
      return placePart || productPart || 'Pesquisa de satisfação'
    },

    productNameHTML() {
      const placeName = this.product.vendor_name || this.product.product_place_name
      const cityName =
        this.product.city && this.product.city.length > 0
          ? this.product.city[0].name
          : ''
      const productPart = this.productName
      const title = [placeName, cityName].filter(Boolean).join(' - ')
      if (!title && !productPart) return ''

      let html = ''
      if (title) {
        html += `<h1 class="survey-product-name">${title}</h1>`
      }
      if (productPart) {
        html += `<h3 class="survey-product-subname">${productPart}</h3>`
      }
      return html
    },

    hasSurveyFields() {
      return Object.keys(this.xFields || {}).length > 0
    },

    placeUuid() {
      return (
        this.product.place_uuid ||
        this.product.placeUUID ||
        this.product.product_place_uuid ||
        (this.place_public_data &&
          this.place_public_data.place &&
          this.place_public_data.place.uuid) ||
        ''
      )
    },
  },

  watch: {
    place_public_data: {
      deep: true,
      handler() {
        this.loadPlaceLogo()
      },
    },
  },

  async mounted() {
    try {
      await this.fetchUserDetails(process.env.VUE_APP_UUID_DEFAULT_VENDOR)
      await this.loadProduct()
      if (!this.productFound) return
      await this.loadPlaceData()
      this.setXFields()
      this.initXFieldsData()
    } catch (error) {
      console.error('Erro ao carregar pesquisa de satisfação:', error)
    } finally {
      this.isLoading = false
    }
  },

  beforeDestroy() {
    this.clearPlaceLogoObjectUrl()
  },

  methods: {
    ...mapActions('auth', ['fetchUserDetails']),
    ...mapActions('product', ['getPOIByUUID','pingSurveyVisitors']),
    ...mapActions('booking', ['fetchPlacePublicData']),

    async loadPlaceData() {
      const placeUuid =
        this.product.place_uuid ||
        this.product.placeUUID ||
        this.product.product_place_uuid ||
        null

      if (!placeUuid) return

      try {
        await this.fetchPlacePublicData(placeUuid)
        await this.loadPlaceLogo()
      } catch (error) {
        console.warn('Não foi possível carregar os dados do gestor:', error)
      }
    },

    clearPlaceLogoObjectUrl() {
      if (this.placeLogoObjectUrl) {
        URL.revokeObjectURL(this.placeLogoObjectUrl)
        this.placeLogoObjectUrl = null
      }
    },

    async loadPlaceLogo() {
      this.clearPlaceLogoObjectUrl()
      this.placeLogoUrl = null

      if (!this.productFound) return

      const resolved = publicLogoSource(this.place_public_data)

      if (!resolved || !resolved.src) {
        return
      }

      if (resolved.type === 'url') {
        this.placeLogoUrl = resolved.src
        return
      }

      try {
        const response = await nodeService.getFileByPath(resolved.src)
        const objectUrl = URL.createObjectURL(
          new Blob([response.data], { type: response.data.type })
        )
        this.placeLogoObjectUrl = objectUrl
        this.placeLogoUrl = objectUrl
      } catch (error) {
        console.warn('Não foi possível carregar o logotipo do gestor:', error)
      }
    },

    setXFields() {
      let surveyXFields =
        this.product.configuration && this.product.configuration.survey_fields
          ? JSON.parse(JSON.stringify(this.product.configuration.survey_fields))
          : {}

      let xFields = {}
      Object.keys(surveyXFieldsFallback).forEach((key) => {
        xFields[key] = JSON.parse(JSON.stringify(surveyXFieldsFallback[key]))
        if (surveyXFields[key]) {
          xFields[key] = {
            ...xFields[key],
            ...surveyXFields[key],
          }
        }
      })

      this.xFields = xFields
    },

    async loadProduct() {
      if (!this.productUuid) {
        this.product = {}
        return
      }
      try {
        let response =
          (await this.getPOIByUUID({ PRODUCT_UUID: this.productUuid })) || {}
        this.product = response[0] || {}
        if (!this.product.uuid) return
      } catch (error) {
        console.warn(
          'Não foi possível carregar o produto da pesquisa:',
          this.productUuid,
          error
        )
        this.product = {}
        return
      }

      try {
        await this.pingSurveyVisitors({
          PRODUCT_UUID: this.productUuid,
          COLLECTOR_ID: this.collectorId,
        })
      } catch (error) {
        console.warn('Não foi possível registrar a visita da pesquisa:', error)
      }
    },

    initXFieldsData() {
      const data = {}
      Object.keys(this.xFields || {}).forEach((key) => {
        const field = this.xFields[key] || {}
        data[key] = {
          ...JSON.parse(JSON.stringify(field)),
          value:
            field.value != null
              ? field.value
              : field.type === 'slider'
              ? (field.options && field.options.value) || 50
              : '',
        }
      })
      this.xFieldsData = data
    },

    formatAnswerValue(field) {
      if (!field) return ''
      const value = field.value
      if (value == null || value === '') return ''

      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          return value
            .map((item) =>
              item && typeof item === 'object'
                ? item.label || item.value || item.id || JSON.stringify(item)
                : String(item)
            )
            .join(', ')
        }
        return value.label || value.value || value.id || JSON.stringify(value)
      }

      return String(value)
    },

    cleanResponseTime(value) {
      return String(value || '')
        .replace(/[()]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    },

    createResponseId() {
      const alphabet =
        'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
      let id = ''
      for (let i = 0; i < 8; i++) {
        id += alphabet.charAt(Math.floor(Math.random() * alphabet.length))
      }
      return id
    },

    internalCollectorId() {
      const productId =
        this.product &&
        (this.product.id != null
          ? this.product.id
          : this.product.product_id != null
          ? this.product.product_id
          : null)

      if (productId == null || productId === '') return 'INTERNAL_0'
      return `INTERNAL_${productId}`
    },

    resolveCollector() {
      const routeCollector = String(this.collectorId || '').trim()
      const collectorId = routeCollector || this.internalCollectorId()

      const placeCollector = PLACE_COLLECTORS[this.placeUuid] || null
      const collectorName =
        (placeCollector && placeCollector.collectorName) ||
        `Satisfação ${this.placeName || this.productName}`

      return { collectorId, collectorName }
    },

    buildSurveyPayload(formPayload) {
      const { collectorId, collectorName } = this.resolveCollector()
      const body = {
        collectorName,
        status: 'COMPLETED',
        surveyId: this.productUuid || collectorId,
        collectorId,
        responseId: this.createResponseId(),
        startTime: moment().format('MMM D, YYYY HH:mm:ss'),
        place_uuid: this.placeUuid || '',
        product_uuid: this.productUuid || '',
      }

      Object.keys(formPayload || {}).forEach((slug) => {
        const field = formPayload[slug] || {}
        if (
          field.type === 'hidden_config' ||
          String(slug).indexOf('session_title') === 0 ||
          String(slug).indexOf('page_') === 0
        ) {
          return
        }

        if (slug === 'responseTime') {
          const cleaned = this.cleanResponseTime(field.value)
          if (cleaned) body.responseTime = cleaned
          return
        }

        const answer = this.formatAnswerValue(field)
        if (answer === '' || answer == null) return
        body[slug] = answer
      })

      return body
    },

    async onSubmitSurvey(payload) {
      if (this.isSubmitting || this.isSubmitted) return

      const surveyPayload = this.buildSurveyPayload(payload)

      if (!surveyPayload.collectorId) {
        this.$notify({
          type: 'danger',
          message: 'Não foi possível identificar o coletor desta pesquisa.',
          duration: 5000,
        })
        return
      }

      this.isSubmitting = true

      try {
        await nodeService.registerSurveyResponse(surveyPayload)
        this.isSubmitted = true
        this.$notify({
          type: 'success',
          message: 'Obrigado! Sua avaliação foi enviada com sucesso.',
          duration: 4000,
        })
      } catch (error) {
        console.error('Erro ao registrar pesquisa de satisfação:', error)
        this.$notify({
          type: 'danger',
          message:
            'Não foi possível enviar sua avaliação. Tente novamente em instantes.',
          duration: 5000,
        })
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style>
.survey-page {
  min-height: 100vh;
  background-color: #f5f9f9;
}

.survey-page-wrapper {
  padding-bottom: 2.5rem;
}

.survey-container {
  width: 100%;
  max-width: 70rem;
  margin: 0 auto;
  padding: 0 24px;
}

.survey-hero .sales-page-profile {
  margin: 0;
}

.survey-hero .sales-page-profile__cover {
  height: 110px;
  background-image: linear-gradient(87deg, #172b4d 0, #12576e 100%);
}

.survey-hero .sales-page-profile__card {
  position: relative;
  margin: -4.5rem 0 0;
  padding: 0 1.25rem 1.5rem;
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
  text-align: center;
}

.survey-hero .sales-page-profile__logo {
  width: 7rem;
  height: 7rem;
  margin: -3.5rem auto 1rem;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #fff;
  background: #fff;
  box-shadow: 0 4px 12px rgba(20, 103, 130, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
}

.survey-hero .sales-page-profile__logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.survey-content {
  margin-top: 24px;
}

.survey-card {
  background: #fff;
  border-radius: 0.5rem;
  border: 1px solid #e6eef2;
  box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
  padding: 1.5rem 1.5rem 1.25rem;
}

.survey-invalid-message {
  margin: 0 auto;
  max-width: 32rem;
  color: #12576e;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.45;
}

.survey-title-block {
  margin-bottom: 0.15rem;
}

.survey-product-name {
  margin: 0 0 0.15rem;
  color: #12576e;
  font-size: 1.35rem;
  font-weight: 700;
}

.survey-product-subname {
  margin: 0 auto;
  max-width: 32rem;
  color: #525f7f;
  font-size: 0.95rem;
  font-weight: 400;
  line-height: 1.5;
}

.survey-subtitle {
  color: #6c757d;
  font-size: 0.95rem;
  margin-bottom: 0;
}

.survey-divider {
  border: 0;
  border-top: 2px solid #12576e;
  opacity: 0.35;
  margin: 1.1rem 0;
}

.survey-form-body {
  min-height: 120px;
}

.survey-success-icon {
  font-size: 3rem;
  color: #20824f;
}

.survey-success-title {
  color: #12576e;
  font-size: 1.35rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.survey-footer {
  margin-top: 0.5rem;
}

@media (max-width: 767.98px) {
  .survey-container {
    padding: 0 16px;
  }

  .survey-hero .sales-page-profile__logo {
    width: 5.5rem;
    height: 5.5rem;
    margin-top: -2.75rem;
  }

  .survey-hero .sales-page-profile__name,
  .survey-product-name {
    font-size: 1.2rem;
  }

  .survey-card {
    padding: 1rem 0.7rem 0.9rem;
  }

  .survey-product-subname {
    font-size: 0.95rem;
  }

  .survey-divider {
    margin: 0.9rem 0;
  }
}
</style>
